import { runSingleFlight, type SingleFlightSlot } from '@/lib/async/singleFlight';

/**
 * Hotfix 1026 / CAUSE-B F2: the access read recovers BY ITSELF after a network drop.
 *
 * The incident: supabase-js runs getUser() inside an exclusive cross-tab lock. One call hung on a dead socket, every
 * later read (Retry clicks, revalidations) queued ANOTHER lock waiter behind it, each gave up after 15 s, and nothing
 * re-read by itself, so the tab showed "Couldn't load your access" for minutes after the network came back.
 *
 * Three small pieces, each with one job (AppShellProvider wires them; AuthGuard only reads the classification):
 *   - SingleFlight: while one call is in flight, later callers await it instead of starting another.
 *   - BackoffScheduler: one pending re-read at a time, on a fixed delay ladder (2, 5, 10, 20, then 30 s capped).
 *   - AccessReadError / isTransientAccessFailure: a 401/403 (or any other definite refusal) is final and is never
 *     retried; a network failure, a timeout or a 5xx is transient and is re-read until it succeeds or the user signs out.
 * Fail-closed (Q-846) is untouched: a failed read still clears every grant; recovery only ever re-reads.
 */

/** Silent re-read delays after a transient failure; the last one repeats (the cap). */
export const ACCESS_REREAD_BACKOFF_MS: readonly number[] = [2_000, 5_000, 10_000, 20_000, 30_000];

/** While a call is in flight, every caller gets that same promise; the next call starts only after it settles. */
export class SingleFlight<T> {
  private readonly slot: SingleFlightSlot<T> = { current: null };

  /**
   * An auth event needs a read begun after that event, rather than an older call's user. Wait here in JavaScript
   * before entering Supabase's lock, then share any newer call. A retired event must not start another read.
   */
  async runFresh(start: () => Promise<T>, isCurrent: () => boolean): Promise<T | undefined> {
    const earlier = this.slot.current;
    if (earlier) {
      try {
        await earlier;
      } catch {
        // The old call's failure belongs to its callers; this event needs a fresh read.
      }
    }
    if (!isCurrent()) return undefined;
    return this.run(start);
  }

  run(start: () => Promise<T>): Promise<T> {
    return runSingleFlight(this.slot, start);
  }
}

/**
 * At most one scheduled task. Each schedule() waits the next delay of the ladder (the last delay repeats);
 * reset() after a success starts the ladder again.
 */
export class BackoffScheduler {
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private task: (() => void) | null = null;

  constructor(private readonly delaysMs: readonly number[]) {}

  /**
   * Use the next backoff delay, with the cap covering the failed read as well as the wait.
   * Otherwise a 10 s auth deadline followed by the 30 s cap strands a recovered network for 40 s.
   */
  schedule(task: () => void, readElapsedMs = 0): void {
    this.cancel();
    const cap = this.delaysMs[this.delaysMs.length - 1];
    const backoff = this.delaysMs[Math.min(this.attempt, this.delaysMs.length - 1)];
    const delay = Math.min(backoff, Math.max(0, cap - readElapsedMs));
    this.attempt += 1;
    this.task = task;
    this.timer = setTimeout(() => this.fire(), delay);
  }

  /** Run the pending task now (e.g. the browser came back online). False when nothing is pending. */
  runNow(): boolean {
    if (!this.task) return false;
    this.fire();
    return true;
  }

  get isPending(): boolean {
    return this.task !== null;
  }

  /** Drop the pending task; the ladder position is kept (a re-read started by other means is still an attempt). */
  cancel(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.task = null;
  }

  /** Drop the pending task and start the ladder again (after a success, a final failure or sign-out). */
  reset(): void {
    this.cancel();
    this.attempt = 0;
  }

  private fire(): void {
    const task = this.task;
    this.cancel();
    task?.();
  }
}

/** A failed access read whose retry-ability is known (an HTTP refusal or an auth error with a status). */
export class AccessReadError extends Error {
  readonly transient: boolean;

  constructor(message: string, transient: boolean) {
    super(message);
    this.name = 'AccessReadError';
    this.transient = transient;
  }

  /** From an HTTP-like status: no status / 0 (network), 408, 429 and 5xx are transient; other answers are final. */
  static fromStatus(message: string, status: number | undefined): AccessReadError {
    const transient = !status || status === 408 || status === 429 || status >= 500;
    return new AccessReadError(message, transient);
  }
}

/**
 * Whether a failed read is worth re-reading by itself. Errors with no known status (a rejected fetch, a timeout) are
 * transient; an AccessReadError says so itself.
 */
export function isTransientAccessFailure(error: unknown): boolean {
  if (!error) return false;
  return error instanceof AccessReadError ? error.transient : true;
}

/** The name and message of an error for a log line (an Error serialises to {} with JSON.stringify). */
export function describeError(error: unknown): { name: string; message: string } {
  if (error instanceof Error) return { name: error.name, message: error.message };
  return { name: typeof error, message: String(error) };
}
